/**
 * useCostSummary — the usage/cost numbers behind the top-bar pill.
 *
 * Each page mounts its own shell (and so its own pill), so the value cannot live in component state or it restarts at
 * zero on every navigation and waits for a click. The latest response is kept at module level (and in sessionStorage
 * for a reload), every mount shows it immediately, and a fetch refreshes it on mount, on a timer and when the tab
 * regains focus.
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'lyra:cost-summary';
const STALE_MS = 20_000;
const POLL_MS = 60_000;

type Snapshot = { data: any; loading: boolean; at: number };

function readStored(): { data: any; at: number } {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) { const v = JSON.parse(raw); if (v?.data) return { data: v.data, at: Number(v.at) || 0 }; }
  } catch (_) { /* private mode or corrupt: start empty */ }
  return { data: null, at: 0 };
}

let snapshot: Snapshot = { ...readStored(), loading: false };
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();
const emit = (next: Partial<Snapshot>) => { snapshot = { ...snapshot, ...next }; listeners.forEach((l) => l()); };

export function refreshCostSummary(): Promise<void> {
  if (inflight) return inflight;
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  if (!token) return Promise.resolve();
  emit({ loading: true });
  inflight = (async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_API_URL ?? ''}/api/usage/cost-summary`, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (d?.success) {
        emit({ data: d, at: Date.now() });
        try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ data: d, at: snapshot.at })); } catch (_) { /* not remembered */ }
      }
    } catch (_) {
      // keep showing the last value; the next poll tries again
    } finally {
      inflight = null;
      emit({ loading: false });
    }
  })();
  return inflight;
}

/** Forget the cached value, e.g. on logout so the next account never sees the previous one's usage. */
export function clearCostSummary() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) { /* nothing stored */ }
  emit({ data: null, at: 0 });
}

const subscribe = (cb: () => void) => { listeners.add(cb); return () => { listeners.delete(cb); }; };
const getSnapshot = () => snapshot;

export default function useCostSummary() {
  const { data, loading, at } = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    if (Date.now() - snapshot.at > STALE_MS) refreshCostSummary();
    const timer = window.setInterval(() => { if (!document.hidden) refreshCostSummary(); }, POLL_MS);
    const onVisible = () => { if (!document.hidden && Date.now() - snapshot.at > STALE_MS) refreshCostSummary(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => { window.clearInterval(timer); document.removeEventListener('visibilitychange', onVisible); window.removeEventListener('focus', onVisible); };
  }, []);

  const refresh = useCallback(() => refreshCostSummary(), []);
  return { data, loading, hasData: !!data, updatedAt: at, refresh };
}
